import {
  AlertOutlined,
  ApiOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  BlockOutlined,
  BuildOutlined,
  ClusterOutlined,
  CodeOutlined,
  CommentOutlined,
  ContainerOutlined,
  CreditCardOutlined,
  FileDoneOutlined,
  FileMarkdownOutlined,
  FundOutlined,
  HistoryOutlined,
  IdcardOutlined,
  LineChartOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ProductOutlined,
  ReconciliationOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  SnippetsOutlined,
  SolutionOutlined,
  StockOutlined,
  TableOutlined,
  TagOutlined,
  TeamOutlined,
  DatabaseOutlined,
  HomeOutlined,
  DownOutlined,
  UpOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import {
  Button,
  ConfigProvider,
  FloatButton,
  Layout,
  Menu,
  Modal,
  Result,
  Select,
  notification,
  Drawer,
  Badge,
  Card,
  Popconfirm,
  Space,
  Tag,
} from "antd";
import ruRU from "antd/locale/ru_RU";
import React, { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import { CookieAcceptNotification } from "./enitities/Cookie";
import {
  AmoTriggers,
  RentalChart,
  Settings,
  TableAnalystics,
  TableCategoriesPage,
  TableTechCardsPage,
  TableContracts,
  TableContragents,
  TableDocsSales,
  TableDocsWarehouse,
  TableEvents,
  TableIntegrations,
  TableLoyalityCards,
  TableLoyalityReport,
  TableLoyalitySettings,
  TableLoyalityTransactions,
  TableNomenclature,
  TableOrganizations,
  TablePayboxes,
  TablePrices,
  TablePricesHandsontable,
  TableProjects,
  TablePurchases,
  TableWarehouses,
  TableWarehousesBalance,
  TableFeeds,
  TableChats,
  TablePromocode,
  CommerceML,
  LoyaltyQRPage,
  QRPublicLanding,
  BlogPage
} from "./pages";
import { OrderCourier, OrderGeneral, OrderPicker } from "./pages/Orders";
import Segments from "./pages/Segments/Segments";
import SegmentsDetails from "./pages/SegmentsDetails/SegmentsDeatails";
import TableContragentsHandsontable from "./pages/TableContragentsHandsontable/TableContragentsHandsontable";
import PaymentsWrapper from "./pages/TablePayments/PaymentsWrapper";
import TablePriceType from "./pages/TablePriceType/ui/TablePriceType";
import { AuthError, CBSelect, LoadingState } from "./shared";
import { apiRequests } from "./shared/api/api";
import Loading from "./shared/ui/Loading";
import CashboxSettings from "./pages/TableSettings/CashboxSettings/CashboxSettings";
import UsersPage from "./pages/TableUsers/UserPage";
import ConnectWalletCard from "./pages/AppleWallet/ConnectWalletCard";
import { playNotificationSound } from "./pages/TableChats/utils";
import { WebSocketManager } from "./pages/TableChats/webSocket";
import { Toaster } from "sonner";
import { FileCode } from "lucide-react";
import { AppTour } from "./features/Tour/AppTour";

function AllChatsWsWhenAuthenticated({ token }) {
  useEffect(() => {
    if (!token) return;
    const ws = WebSocketManager.getInstance();
    ws.setCallbacks({
      onNewMessage: (msg) => {
        if (String(msg?.sender_type || "").toUpperCase() === "CLIENT")
          playNotificationSound();
        queryClient.invalidateQueries({ queryKey: ["chats", token] });
      },
      onChatUpdated: () =>
        queryClient.invalidateQueries({ queryKey: ["chats", token] }),
      onTypingUpdate: () => {},
      onOnlineUpdate: () => {},
      onLastMessageUpdate: () => {},
    });
    ws.connectToAllChats(token, {
      onMessage: (data) => {
        const h = ws.createAllChatsHandler();
        if (h) h(data);
      },
      onOpen: () => {},
      onClose: () => {},
      onError: () => {},
    });
    return () => ws.disconnectAllChats();
  }, [token]);
  return null;
}

const AppWrapper = () => {
  const location = useLocation();
  const isQrLandingPage = location.pathname.match(/^\/qr\/[a-zA-Z0-9]+$/);

  if (isQrLandingPage) {
    const code = location.pathname.split('/').pop();
    return (
      <ConfigProvider locale={ruRU}>
        <QueryClientProvider client={queryClient}>
          <QRPublicLanding code={code} />
        </QueryClientProvider>
      </ConfigProvider>
    );
  }

  return <App />;
};

const { Option } = Select;
const { Header, Content, Footer, Sider } = Layout;

const urlSearchParams = new URLSearchParams(window.location.search);
const params = Object.fromEntries(urlSearchParams.entries());
const queryClient = new QueryClient();

const nav_items = (token, unreadCount = 0) => [
  {
    label: <Link to={`/?token=${token}`}>Платежи</Link>,
    key: "payments",
    icon: <LineChartOutlined />,
  },

  {
    label: <Link to={`/payboxes?token=${token}`}>Счета</Link>,
    key: "payboxes",
    icon: <CreditCardOutlined />,
  },

  {
    label: <Link to={`/analytecs?token=${token}`}>Аналитика</Link>,
    key: "analytecs",
    icon: <StockOutlined />,
  },

  {
    label: "Продажи",
    key: "sales",
    icon: <AuditOutlined />,
    children: [
      {
        label: <Link to={`/docs_sales?token=${token}`}>Продажи</Link>,
        key: "docs_sales",
        icon: <SnippetsOutlined />,
      },
      {
        label: <Link to={`/contracts?token=${token}`}>Договоры</Link>,
        key: "contracts",
        icon: <SnippetsOutlined />,
      },
    ],
  },

  {
    label: <Link to={`/docs_purchases?token=${token}`}>Закупки</Link>,
    key: "docs_purchases",
    icon: <ShoppingCartOutlined />,
  },

  {
    label: "Склад",
    key: "warehouse",
    icon: <ProductOutlined />,
    className: "tour-btn-add-room",
    children: [
      {
        label: <Link to={`/docs_warehouse?token=${token}`}>Документы</Link>,
        key: "docs_warehouse",
        icon: <SnippetsOutlined />,
      },
      {
        label: <Link to={`/warehouses?token=${token}`}>Помещения</Link>,
        key: "warehouses",
        icon: <ShopOutlined />,
      },
      {
        label: <Link to={`/warehouses_balances?token=${token}`}>Остатки</Link>,
        key: "warehouses_balances",
        icon: <BlockOutlined />,
      },
    ],
  },
  {
    label: "Товары и услуги",
    key: "goods_and_services",
    icon: <ShoppingOutlined />,
    className: "tour-btn-goods",
    children: [
      {
        label: <Link to={`/prices?token=${token}`}>Цены</Link>,
        key: "prices",
        icon: <SnippetsOutlined />,
      },
      {
        label: <Link to={`/price_types?token=${token}`}>Типы цен</Link>,
        key: "price_types",
        icon: <ReconciliationOutlined />,
      },
      {
        label: (
          <Link to={`/nomenclature_handsontable?token=${token}`}>Данные</Link>
        ),
        key: "nomenclature_handsontable",
        icon: <TableOutlined />,
      },
      {
        label: <Link to={`/categories?token=${token}`}>Категории</Link>,
        key: "categories",
        icon: <ContainerOutlined />,
      },
      {
        label: <Link to={`/tech_cards?token=${token}`}>Тех. карты</Link>,
        key: "tech_cards",
        icon: <DatabaseOutlined />,
      },
      {
        label: <Link to={`/nomenclature?token=${token}`}>Номенклатура</Link>,
        key: "nomenclature",
        icon: <FileDoneOutlined />,
      },
      {
        label: <Link to={`/feeds?token=${token}`}>Фиды</Link>,
        key: "feeds",
        icon: <FileCode className="w-4 h-4" />,
      },
    ],
  },
  {
    label: "Карты лояльности",
    key: "loyality_settings",
    icon: <CreditCardOutlined />,
    children: [
      {
        label: <Link to={`/loyality_cards?token=${token}`}>Список</Link>,
        key: "loyality_cards",
        icon: <SnippetsOutlined />,
      },
      {
        label: (
          <Link to={`/loyality_transactions?token=${token}`}>Транзакции</Link>
        ),
        key: "loyality_transactions",
        icon: <BarChartOutlined />,
      },
      {
        label: <Link to={`/loyality_settings?token=${token}`}>Настройки</Link>,
        key: "loyality_settings",
        icon: <SettingOutlined />,
      },
      {
        label: <Link to={`/analytics_cards?token=${token}`}>Отчеты</Link>,
        key: "analytics_cards",
        icon: <ClusterOutlined />,
      },
      {
        label: <Link to={`/loyalty_qr?token=${token}`}>QR Лояльность</Link>,
        key: "loyalty_qr",
        icon: <QrcodeOutlined />,
      },
    ],
  },
  {
    label: "Контрагенты",
    key: "contragents",
    icon: <IdcardOutlined />,
    children: [
      {
        label: <Link to={`/contragents?token=${token}`}>Список</Link>,
        key: "contragents",
        icon: <SolutionOutlined />,
      },
      {
        label: (
          <Link to={`/contragents_handsontable?token=${token}`}>Данные</Link>
        ),
        key: "contragents_handsontable",
        icon: <TableOutlined />,
      },
    ],
  },
  {
    label: <Link to={`/integrations?token=${token}`}>Интеграции</Link>,
    key: "integrations",
    icon: <ApiOutlined />,
    className: "tour-btn-integrations",
  },
  {
    label: <Link to={`/blog?token=${token}`}>Блог</Link>,
    key: "blog",
    icon: <FileMarkdownOutlined />,
  },
  {
    label: (
      <Link to={`/chats?token=${token}`}>
        <Space size={4}>
          <span>Чаты</span>
        </Space>
      </Link>
    ),
    key: "chats",
    className: "tour-btn-chats",
    icon: (
      <>
        {unreadCount > 0 && (
          <Badge
            count={unreadCount < 99 ? unreadCount : "99"}
            size="small"
            style={{
              zIndex: 100,
              left: 10,
              bottom: 5,
              position: "absolute",
              paddingLeft: 2,
              fontSize: "10px",
              width: "14px",
              height: "14px",
              lineHeight: "13px",
              textAlign: "center",
              backgroundColor: "#ff4d4f",
            }}
          />
        )}
        <CommentOutlined zIndex={1} />
      </>
    ),
  },
  {
    label: <Link to={`/projects?token=${token}`}>Проекты</Link>,
    key: "projects",
    icon: <FundOutlined />,
  },
  {
    label: <Link to={`/users?token=${token}`}>Пользователи</Link>,
    key: "users",
    icon: <TeamOutlined />,
    className: "tour-btn-invite",
  },
  {
    label: <Link to={`/events?token=${token}`}>События</Link>,
    key: "events",
    icon: <HistoryOutlined />,
  },

  {
    label: <Link to={`/organizations?token=${token}`}>Организации</Link>,
    key: "organizations",
    icon: <BuildOutlined />,
    className: "tour-btn-add-org",
  },

  {
    label: <Link to={`/segments?token=${token}`}>Сегменты</Link>,
    key: "segments",
    icon: <TagOutlined />,
  },

  {
    label: <Link to={`/rent?token=${token}`}>Аренда</Link>,
    key: "rent",
    icon: <TableOutlined />,
  },
  {
    label: <Link to={`/settings?token=${token}`}>Настройки</Link>,
    key: "settings",
    icon: <SettingOutlined />,
  },
  {
    label: (
      <a
        href={`https://${process.env.REACT_APP_APP_URL}/api/v1/docs`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Swagger API
      </a>
    ),
    key: "swaggerapi",
    icon: <CodeOutlined />,
  },
  {
    label: (
      <a
        href={`https://${process.env.REACT_APP_FRONT_URL}/docs/intro/`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Документация
      </a>
    ),
    key: "api",
    icon: <FileMarkdownOutlined />,
  },
];

// Компонент для страниц заказов, который не требует токен в URL
const OrdersRoutes = () => {
  return (
    <Routes>
      <Route path="/orders/:id" element={<OrderGeneral />} />
      <Route path="/orders/:id/picker" element={<OrderPicker />} />
      <Route path="/orders/:id/courier" element={<OrderCourier />} />
    </Routes>
  );
};

const AppleWalletRoutes = () => {
  return (
    <Routes>
      <Route path="get_apple_wallet_card" element={<ConnectWalletCard />} />
    </Routes>
  );
};

const App = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [loaded, setLoaded] = useState([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);
  const [authError403, setAuthError403] = useState(false);
  const [CBInfoText, setCBInfoText] = useState(
    <span style={{ marginRight: 10 }}>
      <BankOutlined /> <b>Загрузка...</b>{" "}
    </span>,
  );
  const [CBInfo, setCBInfo] = useState({});
  const [inviteToken, setInviteToken] = useState();
  const [isAdmin, setIsAdmin] = useState(false);
  const [payboxesData, setPayboxesData] = useState({});
  const [paymentsData, setPaymentsData] = useState({});
  const [projectData, setProjectData] = useState({});
  const [usersData, setUsersData] = useState({});
  const [loyalityData, setLoyalityData] = useState({});
  const [organizationsData, setOrganizationsData] = useState({});
  const [loyalityTransactionsData, setLoyalityTransactionsData] = useState({});
  const [conteragentsData, setConteragentsData] = useState({});
  const [categoriesData, setCategoriesData] = useState({});
  const [tech_cardsData, setTechCardsData] = useState({});
  const [nomenclatureData, setNomenclatureData] = useState({});
  const [manufacturersData, setManufacturersData] = useState({});
  const [unitsData, setUnitsData] = useState({});
  const [contractsData, setContractsData] = useState({});
  const [warehousesData, setWarehousesData] = useState({});
  const [warehousesDocsData, setWarehousesDocsData] = useState({});
  const [pricesData, setPricesData] = useState({});
  const [priceTypeData, setPriceTypeData] = useState({});
  const [loyalitySettingsData, setLoyalitySettingsData] = useState({});
  const [totalUnreadChats, setTotalUnreadChats] = useState(0);

  const fetchData = () => {
    let Loading = loaded.slice();
    console.log("init");
    apiRequests.cashboxes
      .getMeta()
      .then((response) => {
        var options_list = [];
        for (var i in response.data.cboxes) {
          options_list.push({
            label: (
              <a
                href={
                  `https://${process.env.REACT_APP_APP_URL}/?token=` +
                  response.data.cboxes[i].token
                }
                target="_self"
                rel="noopener noreferrer"
              >
                {response.data.cboxes[i].name}
              </a>
            ),
            key: response.data.cboxes[i].name,
          });
        }

        setCBInfo(response.data.cboxes[0]);
        setLoggedIn(true);
        setCBInfoText(
          <CBSelect
            token={params.token}
            CBOptions={options_list}
            CBData={response.data.cboxes}
          ></CBSelect>,
        );
        setInviteToken(response.data.invite_token);
        apiRequests.payboxes
          .getItems({ token: params.token, limit: 35, offset: 0 })
          .then((response) => {
            var options_list = [];
            for (var i in response.data.result) {
              let color = "green";
              if (response.data.result[i].balance < 0) {
                color = "red";
              }
              options_list.push(
                <Option key={response.data.result[i].id}>
                  {response.data.result[i].name}:{" "}
                  <font color={color}>
                    {response.data.result[i].balance} руб.
                  </font>
                </Option>,
              );
            }

            const uniquePayboxes = response.data.result.map((item) => {
              return { value: item.name };
            });

            setPayboxesData({
              payboxesCount: response.data.count,
              payboxesDS: response.data.result,
              payboxesSelect: options_list,
              payboxesMeta: uniquePayboxes,
            });
          });

        apiRequests.payments
          .getItems({ ...params, limit: 35, offset: 0 })
          .then((response) => {
            const nameValues = [
              ...new Set(response.data.result.map((item) => item.name)),
            ].filter((x) => x !== null);
            const tagValues = [
              ...new Set(response.data.result.map((item) => item.tags)),
            ].filter((x) => x !== null);

            const mergedTags = [
              ...new Set(
                [].concat.apply(
                  [],
                  tagValues.map((item) => item.split(",")),
                ),
              ),
            ];

            const uniqueNameValues = nameValues.map((item) => {
              return { value: item, label: item };
            });
            const uniqueTagValues = mergedTags.map((value, i) => {
              return {
                label: `${value}`,
                value: i,
              };
            });
            // const uniqueArticleValues = articleValues.map(item => { return { value: item } });
            const paymentCalendar = [
              ...new Set(response.data.result.map((item) => item)),
            ];

            const { Option } = Select;
            const children = [];
            for (let i = 0; i < uniqueTagValues.length; i++) {
              children.push(
                <Option key={uniqueTagValues[i].value}>
                  {uniqueTagValues[i].value}
                </Option>,
              );
            }

            const metaDict = { names: uniqueNameValues, tags: uniqueTagValues };

            Loading[0] = true;
            setLoaded(Loading);

            setPaymentsData({
              paymentsCount: response.data.count,
              paymentsDS: response.data.result,
              paymentsMeta: metaDict,
              listPayment: paymentCalendar,
            });
          });

        apiRequests.projects
          .getItems({ token: params.token, limit: 35, offset: 0 })
          .then((response) => {
            var options_list = [];

            options_list.push(<Option key={0}>Не указывать</Option>);

            for (var i in response.data.result) {
              options_list.push(
                <Option key={response.data.result[i].id}>
                  {response.data.result[i].id}. {response.data.result[i].name}:{" "}
                  {response.data.result[i].proj_sum} руб.
                </Option>,
              );
            }

            const uniqueProjects = response.data.result.map((item) => {
              return { value: item.name };
            });

            Loading[1] = true;
            setLoaded(Loading);

            setProjectData({
              projectsCount: response.data.count,
              projectsDS: response.data.result,
              projectsSelect: options_list,
              projectsMeta: uniqueProjects,
            });
          });

        apiRequests.users
          .getItems({ token: params.token, limit: 100, offset: 0 })
          .then((response) => {
            Loading[2] = true;
            setLoaded(Loading);

            setUsersData({
              usersCount: response.data.count,
              usersDS: response.data.result,
            });
          });

        apiRequests.loyalityCards
          .getItems({ token: params.token, limit: 100, offset: 0 })
          .then((response) => {
            Loading[3] = true;
            setLoaded(Loading);

            setLoyalityData({
              loyalityCount: response.data.count,
              loyalityDS: response.data.result,
            });
          });

        apiRequests.organizations
          .getItems({ token: params.token, limit: 100, offset: 0 })
          .then((response) => {
            Loading[4] = true;
            let options_list = [];
            for (var i in response.data.result) {
              options_list.push({
                value: `${response.data.result[i].id}`,
                label: response.data.result[i].short_name,
              });
            }

            setLoaded(Loading);

            setOrganizationsData({
              organizationsCount: response.data.length,
              organizationsDS: options_list,
            });
          });

        apiRequests.loyalityTransactions
          .getItems({ token: params.token, limit: 100, offset: 0 })
          .then((response) => {
            Loading[5] = true;
            setLoaded(Loading);

            setLoyalityTransactionsData({
              loyalityTransactionsCount: response.data.count,
              loyalityTransactionsDS: response.data.result,
            });
          });

        apiRequests.contragents
          .getItems({ token: params.token, limit: 35, offset: 0 })
          .then((response) => {
            var options_list = [];

            options_list.push(<Option key={0}>Не указывать</Option>);

            for (var i in response.data.result) {
              options_list.push(
                <Option key={response.data.result[i].id}>
                  {response.data.result[i].name}
                </Option>,
              );
            }

            Loading[6] = true;
            setLoaded(Loading);

            const nameValues = [
              ...new Set(response.data.result.map((item) => item.name)),
            ].filter((x) => x !== null);
            const PhoneValues = [
              ...new Set(response.data.result.map((item) => item.phone)),
            ].filter((x) => x !== null);
            const InnValues = [
              ...new Set(response.data.result.map((item) => item.inn)),
            ].filter((x) => x !== null);

            const uniqueNameValues = nameValues.map((item) => {
              return { value: item };
            });
            const uniquePhoneValues = PhoneValues.map((item) => {
              return { value: item };
            });
            const uniqueInnValues = InnValues.map((item) => {
              return { value: item };
            });

            setConteragentsData({
              conteragentsCount: response.data.count,
              conteragentsDS: response.data.result,
              conteragentsSelect: options_list,
              conteragentsAC: {
                names: uniqueNameValues,
                phones: uniquePhoneValues,
                inns: uniqueInnValues,
              },
            });

            apiRequests.chats
              .getChats({ token: params.token, limit: 1000, offset: 0 })
              .then((response) => {
                if (response.data) {
                  const totalUnread = response.data.reduce((sum, chat) => {
                    return sum + (chat.unread_count || 0);
                  }, 0);
                  setTotalUnreadChats(totalUnread);
                } else {
                }
              })
              .catch((error) => {
                console.error("Ошибка загрузки счетчика чатов:", error);
              });

            apiRequests.categories.getItems({ limit: 500 }).then((res) => {
              Loading[7] = true;
              setLoaded(Loading);

              setCategoriesData({
                categoriesDS: res.data.result,
                categoriesCount: res.data.count,
              });
            });

            apiRequests.tech_cards.getItems({ limit: 500 }).then((res) => {
              Loading[7] = true;
              setLoaded(Loading);

              setTechCardsData({
                tech_cardsDS: res.data.result,
                tech_cardsCount: res.data.count,
              });
            });

            apiRequests.nomenclature.getItems().then((res) => {
              Loading[8] = true;
              setLoaded(Loading);

              setNomenclatureData({
                nomenclatureDS: res.data.result,
                nomenclatureCount: res.data.count,
              });
            });
            apiRequests.manufacturers.getItems().then((res) => {
              Loading[9] = true;
              setLoaded(Loading);

              setManufacturersData({
                manufacturersDS: res.data.result,
                manufacturersCount: res.data.count,
              });
            });
            apiRequests.units
              .getItems({ token: params.token, limit: 500 })
              .then((res) => {
                Loading[10] = true;
                setLoaded(Loading);

                setUnitsData({
                  unitsDS: res.data.result,
                  unitsCount: res.data.count,
                });
              });
            apiRequests.organizations.getItems().then((res) => {
              Loading[11] = true;
              setLoaded(Loading);

              setOrganizationsData({
                organizationsDataDS: res.data.result,
                organizationsDataCount: res.data.count,
              });
            });
            apiRequests.contracts.getItems().then((res) => {
              Loading[12] = true;
              setLoaded(Loading);
              setContractsData({
                contractsDS: res.data.result,
                contractsCount: res.data.count,
              });
            });
            apiRequests.warehouses.getItems().then((res) => {
              Loading[13] = true;
              setLoaded(Loading);
              setWarehousesData({
                warehousesDS: res.data.result,
                warehousesCount: res.data.count,
              });
            });
            apiRequests.docs.warehouse().then((res) => {
              Loading[14] = true;
              setLoaded(Loading);
              setWarehousesDocsData({
                warehouseDocsDS: res.data.result,
                warehouseDocsCount: res.data.count,
              });
            });
            // apiRequests.prices.getItems({ ...params, limit: 12 }).then((res) => {
            //   Loading[15] = true;
            //   setLoaded(Loading);
            //   setPricesData({
            //     pricesDS: res.data.result,
            //     pricesCount: res.data.count,
            //   });
            // });
            apiRequests.prices.getTypes().then((res) => {
              Loading[16] = true;
              setLoaded(Loading);
              setPriceTypeData({
                priceTypeDS: res.data.result,
                priceTypeCount: res.data.count,
              });
            });
            apiRequests.loyalitySettings.getItems().then((res) => {
              Loading[17] = true;
              setLoaded(Loading);
              setLoyalitySettingsData({
                loyalitySettingsDS: res.data.result,
                loyalitySettingsCount: res.data.count,
              });
            });

            ws_connect();
          });

        apiRequests.account
          .getInfo()
          .then((response) => {
            setIsAdmin(response.data.is_owner);
            if (
              response.data.demo_left === 0 &&
              response.data.balance <= 0 &&
              !response.data.is_per_user
            ) {
              Modal.confirm({
                title: "Предупреждение",
                content:
                  "Ваш демо-период истек, просим вас оплатить дальнейшее пользование системы",
                okType: "primary",
                okText: "Оплатить",
                cancelText: "Закрыть окно",
                onOk: () => {
                  window.location.href = `https://${response.data.link_for_pay}`;
                },
              });
            }
          })
          .catch((error) => {
            if (error.status === 404) {
              Modal.info({
                title: "Предупреждение",
                content: (
                  <p>
                    В вашем аккаунте не назначен тариф, пожалуйста обратитесь в
                    поддержку.
                  </p>
                ),
                onOk() {},
              });
            }
          });
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          setAuthError403(true);
        }
      });
  };

  useEffect(() => {
    if (params.token) {
      apiRequests.setToken(params.token);
    }
    fetchData();
    if (params.hideLayout) {
      window.document.body.classList.add("hide-layout");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [ws, setWs] = useState(null);
  const [timeout, setTimeout] = useState(250);
  const [notificationsList, setNotificationsList] = useState([]); // история уведомлений
  const [notifDrawerVisible, setNotifDrawerVisible] = useState(false);

  const formatSegmentText = (payload, segmentNameFallback) => {
    if (payload && payload.text) return payload.text;
    const segName = payload?.segment_name || segmentNameFallback || "—";
    const c = payload?.contragent || {};
    const name = c.name || "Неизвестно";
    const phone = c.phone || "Неизвестно";

    if (payload?.type === "contragent_added") {
      return `Новый пользователь добавлен в сегмент!\nСегмент: ${segName}.\nКлиент:\n${name}\nТелефон: ${phone}`;
    }
    if (payload?.type === "contragent_removed") {
      return `Пользователь исключен из сегмента!\nСегмент: ${segName}.\nКлиент:\n${name}\nТелефон: ${phone}`;
    }
    if (payload?.type === "recalc_start") {
      return `Начался пересчёт сегмента "${segName}" (ID: ${payload?.segment_id})`;
    }
    if (payload?.type === "recalc_finish") {
      return `Пересчёт сегмента "${segName}" (ID: ${payload?.segment_id}) успешно завершён`;
    }
    if (payload?.type === "recalc_fail_404") {
      return `Ошибка пересчёта сегмента (ID: ${payload?.segment_id}): сегмент не найден`;
    }
    if (payload?.type === "recalc_fail_410") {
      return `Ошибка пересчёта сегмента (ID: ${payload?.segment_id}): сегмент удалён`;
    }
    // fallback
    return JSON.stringify(payload);
  };

  // Обработка входящего WS сообщения — только события из allowedEvents
  const handleWsMessage = (msg) => {
    const event = msg?.event;
    const payload = msg?.payload || {};

    // Белый список — только эти события допускаются
    const allowedEvents = new Set([
      "segment_member_added",
      "segment_member_removed",
      "recalc_start",
      "recalc_finish",
    ]);

    // Если event отсутствует или не в белом списке — игнорируем сообщение
    if (!event || !allowedEvents.has(event)) {
      console.debug("Ignored WS message (event not allowed):", { event });
      return;
    }

    const segName =
      payload.segment_name || msg.segment_name || CBInfo?.name || "VIP-клиенты";

    let title = "Уведомление";
    let kind = "other";
    if (event === "segment_member_added") {
      title = "Новый пользователь добавлен в сегмент!";
      kind = "added";
    } else if (event === "segment_member_removed") {
      title = "Пользователь исключён из сегмента!";
      kind = "removed";
    } else if (event === "recalc_start") {
      title = "Пересчёт сегмента — старт";
      kind = "recalc";
    } else if (event === "recalc_finish") {
      title = "Пересчёт сегмента — завершён";
      kind = "recalc";
    }

    const text = formatSegmentText(payload, segName);

    // Показываем toast
    notification.open({
      message: title,
      description: <div style={{ whiteSpace: "pre-line" }}>{text}</div>,
      duration: 6,
      placement: "topRight",
    });

    // Сохраняем в историю (макс. 200)
    setNotificationsList((prev) => {
      const next = [
        {
          id: Date.now() + Math.random(),
          event,
          title,
          text,
          kind,
          ts: new Date().toISOString(),
          payload,
        },
        ...prev,
      ];
      return next.slice(0, 200);
    });
  };

  const removeNotification = (id) => {
    setNotificationsList((prev) => prev.filter((n) => n.id !== id));
  };

  const clearNotifications = () => {
    setNotificationsList([]);
  };

  const ws_connect = () => {
    const protocol = process.env.REACT_APP_NODE_ENV === "dev" ? "ws" : "wss";
    const baseUrl = process.env.REACT_APP_APP_URL || window.location.hostname;
    var Ws = new WebSocket(`${protocol}://${baseUrl}/ws/${params.token}/`);
    var connectInterval;

    Ws.onopen = () => {
      console.log("connected websocket main component");

      setWs(Ws);

      setTimeout(250);
      clearTimeout(connectInterval); // clear Interval on on open of websocket connection
    };

    Ws.onmessage = (evt) => {
      try {
        const msg =
          typeof evt.data === "string" ? JSON.parse(evt.data) : evt.data;
        handleWsMessage(msg);
      } catch (err) {
        console.error("WS parse error", err);
      }
    };

    // websocket onclose event listener
    Ws.onclose = (e) => {
      console.log(
        `Socket is closed. Reconnect will be attempted in ${Math.min(
          10000 / 1000,
          (timeout * 2) / 1000,
        )} second.`,
        e.reason,
      );

      setTimeout(timeout * 2);
      connectInterval = setTimeout(check, Math.min(10000, timeout)); //call check function after timeout
    };

    Ws.onerror = (err) => {
      console.error(
        "Socket encountered error: ",
        err.message,
        "Closing socket",
      );

      Ws.close();
    };
  };

  const check = () => {
    if (!ws || ws.readyState === WebSocket.CLOSED) this?.connect();
  };

  const location = useLocation();

  const [selected, setSelected] = useState([]);
  const [loadingChecker, setLoadingChecker] = useState(true);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  useEffect(() => {
    if (loggedIn && loaded && ws) {
      setLoadingChecker(false);
    }
  }, [loggedIn, loaded, ws]);

  useEffect(() => {
    if (!loadingChecker) {
      const pathName = window.location.pathname;
      if (pathName === "/") {
        setSelected(["payments"]);
      } else {
        setSelected([pathName.split("/")[1]]);
      }
    }
  }, [location.pathname, loadingChecker]);

  const isOrdersPage = location.pathname.includes("/orders/");
  const isWalletPage = location.pathname.includes("/get_apple_wallet_card");
  const isChatDetailPage = location.pathname.match(/^\/chats\/\d+$/);

  if (isOrdersPage) {
    return (
      <ConfigProvider locale={ruRU}>
        <OrdersRoutes />
      </ConfigProvider>
    );
  }

  if (isWalletPage) {
    return (
      <ConfigProvider locale={ruRU}>
        <AppleWalletRoutes />
      </ConfigProvider>
    );
  }

  if (!params.token) {
    return (
      <Result
        status="403"
        title={
          <>
            Вы не ввели токен
            <AlertOutlined />
            <br /> Пожалуйста, проверьте вашу ссылку.
          </>
        }
      />
    );
  }

  if (authError403) {
    return <AuthError />;
  }

  const PureRoutes = React.memo(({ query }) => (
    <Routes>
      <Route path="/chats" element={<TableChats query={query} />} />
    </Routes>
  ));

  const routes = (
    <Routes>
      <Route
        path="/"
        element={
          <PaymentsWrapper
            PBData={payboxesData.payboxesSelect}
            PRData={projectData.projectSelect}
            payboxesMeta={payboxesData.payboxesMeta}
            paymentsMeta={paymentsData.paymentsMeta}
            projMeta={projectData.projectsMeta}
            query={params}
            caData={conteragentsData.conteragentsDS}
            caSel={conteragentsData.conteragentsSelect}
            caMeta={conteragentsData.conteragentsAC}
            websocket={ws}
          />
        }
      />
      <Route
        path="/settings/cashbox_settings"
        element={<CashboxSettings token={params.token} />}
      />
      <Route
        path="/payboxes"
        element={
          <TablePayboxes
            query={params}
            payboxesData={{
              c: payboxesData.payboxesCount,
              ds: payboxesData.payboxesDS,
            }}
            websocket={ws}
          />
        }
      />
      <Route
        path="/loyality_cards"
        element={
          <TableLoyalityCards
            query={params}
            orgsData={organizationsData.organizationsDataDS}
            loyalityCardsData={{
              c: loyalityData.loyalityCount,
              ds: loyalityData.loyalityDS,
            }}
            websocket={ws}
          />
        }
      />
      <Route
        path="/loyality_transactions"
        element={
          <TableLoyalityTransactions
            query={params}
            loyalityCardsData={loyalityData.loyalityDS}
            loyalityTransactionsData={{
              c: loyalityTransactionsData.loyalityTransactionsCount,
              ds: loyalityTransactionsData.loyalityTransactionsDS,
            }}
            websocket={ws}
          />
        }
      />
      <Route
        path="/analytecs"
        element={
          <TableAnalystics
            paymentsData={paymentsData.paymentsDS}
            listPayment={paymentsData.listPayment}
            websocket={ws}
            query={params}
            filter={true}
          />
        }
      />
      <Route
        path="/projects"
        element={
          <TableProjects
            c={projectData.projectsCount}
            ds={projectData.projectsDS}
            query={params}
            websocket={ws}
          />
        }
      />
      <Route
        path="/integrations"
        element={<TableIntegrations query={params} websocket={ws} />}
      />
      <Route
        path="/events"
        element={
          <TableEvents
            token={params.token}
            websocket={ws}
            users={usersData.usersDS}
          />
        }
      />
      <Route
        path="/users"
        element={
          <UsersPage
            c={usersData.usersCount}
            ds={usersData.usersDS}
            query={params}
            invite={inviteToken}
            websocket={ws}
          />
        }
      />
      <Route
        path="/contragents"
        element={
          <TableContragents
            query={params}
            c={contractsData.conteragentsCount}
            ds={contractsData.conteragentsDS}
            websocket={ws}
            // meta={this.state.CAAC}
          />
        }
      />
      <Route
        path="/categories"
        element={
          <TableCategoriesPage
            token={params.token}
            websocket={ws}
            initialData={categoriesData.categoriesDS}
          />
        }
      />
      <Route
        path="/tech_cards"
        element={
          <TableTechCardsPage
            token={params.token}
            websocket={ws}
            initialData={tech_cardsData.tech_cardsDS}
          />
        }
      />
      <Route
        path="/nomenclature"
        element={
          <TableNomenclature
            token={params.token}
            websocket={ws}
            initialData={nomenclatureData.nomenclatureDS}
            nomenclatureDataCount={nomenclatureData.nomenclatureCount}
            manufacturersData={manufacturersData.manufacturersDS}
            categoriesData={categoriesData.categoriesDS}
            unitsData={unitsData.unitsDS}
          />
        }
      />
      <Route path="/feeds" element={<TableFeeds token={params.token} />} />
      <Route
        path="/promocode"
        element={<TablePromocode token={params.token} />}
      />
      <Route
        path="/loyalty_qr"
        element={<LoyaltyQRPage token={params.token} />}
      />
      <Route
        path="/organizations"
        element={
          <TableOrganizations
            token={params.token}
            websocket={ws}
            initialData={organizationsData.organizationsDataDS}
          />
        }
      />
      <Route
        path="/contracts"
        element={
          <TableContracts
            token={params.token}
            organizationsData={organizationsData.organizationsDataDS}
            conteragentsData={conteragentsData.conteragentsDS}
            websocket={ws}
            initialData={contractsData.contractsDS}
          />
        }
      />
      <Route
        path="/warehouses"
        element={
          <TableWarehouses
            token={params.token}
            websocket={ws}
            initialData={warehousesData.warehousesDS}
          />
        }
      />
      <Route
        path="/warehouses_balances"
        element={
          <TableWarehousesBalance
            isAdmin={isAdmin}
            token={params.token}
            websocket={ws}
            initialData={warehousesData.warehousesDS}
          />
        }
      />
      <Route
        path="/docs_warehouse"
        element={
          <TableDocsWarehouse
            token={params.token}
            query={params}
            websocket={ws}
            initialData={warehousesDocsData.warehouseDocsDS}
            docsCount={warehousesDocsData.warehouseDocsCount}
            warehousesData={warehousesData.warehousesDS}
            nomenclatureData={nomenclatureData.nomenclatureDS}
            organizationsData={organizationsData.organizationsDataDS}
            unitsData={unitsData.unitsDS}
            priceTypesData={priceTypeData.priceTypeDS}
          />
        }
      />
      <Route
        path="/prices"
        element={
          <TablePrices
            params={params}
            token={params.token}
            websocket={ws}
            initialData={pricesData.pricesDS}
            countData={pricesData.pricesCount}
            priceTypesData={priceTypeData.priceTypeDS}
            manufacturersData={manufacturersData.manufacturersDS}
            nomenclatureData={nomenclatureData.nomenclatureDS}
            categoriesData={categoriesData.categoriesDS}
            unitsData={unitsData.unitsDS}
            setPricesData={setPricesData}
          />
        }
      />
      <Route
        path="/price_types"
        element={
          <TablePriceType
            loading={Loading[16]}
            token={params.token}
            priceTypeData={priceTypeData}
            setPriceType={setPriceTypeData}
          />
        }
      />
      <Route
        path="/loyality_settings"
        element={
          <TableLoyalitySettings
            params={params}
            token={params.token}
            websocket={ws}
            initialData={loyalitySettingsData.loyalitySettingsDS}
            organizationsData={organizationsData.organizationsDataDS}
          />
        }
      />
      <Route
        path="/analytics_cards"
        element={
          <TableLoyalityReport
            params={params}
            token={params.token}
            websocket={ws}
          />
        }
      />
      <Route
        path="/docs_sales"
        element={<TableDocsSales query={params} websocket={ws} />}
      />
      <Route
        path="/docs_purchases"
        element={
          <TablePurchases
            contractsData={contractsData.contractsDS}
            warehousesDS={warehousesData.warehousesDS}
            contragentsInfo={conteragentsData.conteragentsDS}
            organizationsDS={organizationsData.organizationsDataDS}
            query={params}
            websocket={ws}
          />
        }
      />
      <Route
        path="/nomenclature_handsontable"
        element={
          <TablePricesHandsontable token={params.token} websocket={ws} />
        }
      />
      <Route
        path="/contragents_handsontable"
        element={
          <TableContragentsHandsontable token={params.token} websocket={ws} />
        }
      />
      <Route path="/rent" element={<RentalChart token={params.token} />} />
      <Route
        path="/settings/amo_triggers"
        element={<AmoTriggers token={params.token} />}
      />
      <Route path="/blog" element={<BlogPage token={params.token}/>}/>
      <Route
        path="/settings/commerceml"
        element={<CommerceML token={params.token} />}
      />
      <Route path="/settings" element={<Settings token={params.token} />} />
      <Route
        path="/nomenclature"
        element={
          <TableNomenclature
            token={params.token}
            websocket={ws}
            initialData={nomenclatureData.nomenclatureDS}
            nomenclatureDataCount={nomenclatureData.nomenclatureCount}
            manufacturersData={manufacturersData.manufacturersDS}
            categoriesData={categoriesData.categoriesDS}
            unitsData={unitsData.unitsDS}
          />
        }
      />
      <Route path="/segments" element={<Segments websocket={ws} />} />
      <Route path="/segments_details/:id/" element={<SegmentsDetails />} />
    </Routes>
  );

  const isPureRoute = location.pathname.includes("/chats");

  if (isPureRoute) {
    return (
      <ConfigProvider locale={ruRU}>
        <QueryClientProvider client={queryClient}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              overflow: "hidden",
            }}
          >
            {/* Кнопка для показа/скрытия header */}
            <Button
              type="text"
              icon={isHeaderVisible ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setIsHeaderVisible(!isHeaderVisible)}
              style={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1002,
                width: 40,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.05)",
                borderRadius: "0 0 4px 4px",
                border: "none",
              }}
            />

            <div
              style={{
                background: "white",
                padding: "0 16px",
                height: 50,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #f0f0f0",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                transform: isHeaderVisible
                  ? "translateY(0)"
                  : "translateY(-100%)",
                transition: "transform 0.2s ease-in-out",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                zIndex: 1001,
              }}
            >
              <Button
                type="text"
                href={`/?token=${params.token}`}
                icon={<HomeOutlined />}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                На главную
              </Button>
            </div>

            <div
              style={{
                flex: 1,
                overflow: "auto",
                paddingTop: isHeaderVisible ? "50px" : "0",
                transition: "padding-top 0.2s ease-in-out",
              }}
            >
              <PureRoutes query={params} />
            </div>
          </div>
        </QueryClientProvider>
      </ConfigProvider>
    );
  }

  if (params.hideLayout) {
    return (
      <ConfigProvider locale={ruRU}>
        {loadingChecker ? (
          <LoadingState />
        ) : (
          <QueryClientProvider client={queryClient}>
            {routes}
          </QueryClientProvider>
        )}
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider locale={ruRU}>
      {loadingChecker ? (
        <LoadingState />
      ) : (
        <QueryClientProvider client={queryClient}>
          <Layout>
            <Sider
              trigger={null}
              collapsible
              collapsed={collapsed}
              style={{
                background: "white",
                height: "100vh",
                overflow: "auto",
                position: "sticky",
                left: 0,
                top: 0,
                bottom: 0,
              }}
            >
              <Menu
                theme="light"
                mode="inline"
                defaultSelectedKeys={selected}
                selectedKeys={selected}
                items={nav_items(params.token, totalUnreadChats)}
              />
            </Sider>
            <CookieAcceptNotification></CookieAcceptNotification>
            <Layout>
              <Header
                style={{
                  padding: 0,
                  background: "white",
                  position: "sticky",
                  top: 0,
                  zIndex: 100,
                }}
              >
                <Button
                  type="text"
                  icon={
                    collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
                  }
                  onClick={() => setCollapsed(!collapsed)}
                  style={{
                    fontSize: "16px",
                    width: 64,
                    height: 64,
                  }}
                />

                <div style={{ float: "right" }}>
                  {/*<Button href={'https://t.me/natfullinrus'} style={{ marginRight: '15px' }} type={'primary'}>*/}
                  {/*  Оплатить*/}
                  {/*</Button>*/}
                  {/* balance info */}
                  {CBInfoText}
                </div>
              </Header>

              <Content className="site-layout">
                {params.token && (
                  <AllChatsWsWhenAuthenticated token={params.token} />
                )}
                <div
                  className="site-layout-background"
                  style={{
                    paddingLeft: isChatDetailPage ? 0 : 15,
                    paddingRight: isChatDetailPage ? 0 : 15,
                    paddingTop: isChatDetailPage ? 0 : 15,
                    minHeight: isChatDetailPage
                      ? "calc(100vh - 64px)"
                      : "83.5vh",
                    height: isChatDetailPage ? "calc(100vh - 64px)" : "auto",
                    overflow: isChatDetailPage ? "hidden" : "visible",
                  }}
                >
                  {routes}
                </div>
              </Content>

              {!isChatDetailPage && (
                <Footer
                  style={{
                    textAlign: "center",
                  }}
                >
                  TableCRM © {new Date().getFullYear()} Платформа для учета
                  финансов
                </Footer>
              )}
            </Layout>

            <FloatButton
              href={`https://t.me/tablecrmhelpbot?start=${CBInfo.name
                ?.split("_")
                .pop()}${location.pathname.slice(1, location.pathname.length)}`}
              icon={<CommentOutlined />}
              trigger="click"
              style={{ right: 24 }}
              tooltip={
                <>
                  Мы на связи с 9:00-19:00 c Пн-Пт.
                  <br />
                  Кликните для перехода в телеграм-бот.
                </>
              }
            />
            {/* Плавающая кнопка для истории уведомлений*/}
            <FloatButton
              style={{ right: 96 }}
              icon={<AlertOutlined />}
              badge={{ count: notificationsList.length }}
              onClick={() => setNotifDrawerVisible(true)}
              tooltip={<span>Уведомления</span>}
            />

            {/* Drawer с историей уведомлений */}
            <Drawer
              title={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>Уведомления сегментов</div>
                  <Space>
                    <Popconfirm
                      title="Удалить все уведомления?"
                      onConfirm={clearNotifications}
                      okText="Да"
                      cancelText="Отмена"
                    >
                      <Button size="small" danger>
                        Удалить всё
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              }
              placement="right"
              width={420}
              onClose={() => setNotifDrawerVisible(false)}
              open={notifDrawerVisible}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {notificationsList.length === 0 ? (
                  <div
                    style={{ color: "#888", textAlign: "center", padding: 24 }}
                  >
                    Уведомлений нет
                  </div>
                ) : (
                  notificationsList.map((item) => {
                    // цвет фона / border по kind
                    let cardStyle = { borderLeft: "4px solid #d9d9d9" };
                    let headerTag = null;
                    if (item.kind === "recalc") {
                      cardStyle = { borderLeft: "4px solid #1890ff" }; // синий
                      headerTag = <Tag>Пересчёт</Tag>;
                    } else if (item.kind === "added") {
                      cardStyle = { borderLeft: "4px solid #52c41a" }; // зелёный
                      headerTag = <Tag color="success">Добавление</Tag>;
                    } else if (item.kind === "removed") {
                      cardStyle = { borderLeft: "4px solid #f5222d" }; // красный
                      headerTag = <Tag color="error">Исключение</Tag>;
                    } else {
                      cardStyle = { borderLeft: "4px solid #8c8c8c" }; // серый
                    }

                    return (
                      <Card
                        key={item.id}
                        size="small"
                        style={{
                          ...cardStyle,
                          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                          borderRadius: 8,
                          padding: 12,
                        }}
                        bodyStyle={{ padding: 0 }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <div style={{ fontWeight: 600 }}>
                                {item.title}
                              </div>
                              {headerTag}
                            </div>
                            <div
                              style={{
                                marginTop: 6,
                                whiteSpace: "pre-line",
                                color: "#333",
                              }}
                            >
                              {item.text}
                            </div>
                            <div
                              style={{
                                marginTop: 8,
                                color: "#888",
                                fontSize: 12,
                              }}
                            >
                              {new Date(item.ts).toLocaleString()}
                            </div>
                          </div>

                          <div
                            style={{
                              marginLeft: 12,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 8,
                            }}
                          >
                            <Popconfirm
                              title="Удалить уведомление?"
                              onConfirm={() => removeNotification(item.id)}
                              okText="Да"
                              cancelText="Отмена"
                            >
                              <Button size="small">Удалить</Button>
                            </Popconfirm>
                          </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            </Drawer>
            <Toaster position="top-center" expand={true} />
            <AppTour />
          </Layout>
        </QueryClientProvider>
      )}
    </ConfigProvider>
  );
};

export default AppWrapper;
